import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import { get as _get, isEmpty as _isEmpty } from 'lodash';

import { featureFlagQuery } from '@amfam/shared/utility/feature-flag/data-access';

import * as fromRoot from '../../core/store';
import { ChangeRequest } from '../models';
import { CoverageChangeRequest } from '../models/coverage-change.model';
import * as fromCoverageChangeContainer from './coverage-change-container.reducer';
import * as fromCoverageChangeState from './coverage-change-state.reducer';
import * as fromPolicyChangeContainer from './policy-change-container.reducer';
import * as fromPolicyChangeState from './policy-change-state.reducer';

export interface PolicyChangeState {
  status: ChangeRequest;
  notification: fromPolicyChangeContainer.State;
}

export const policyChangereducers: ActionReducerMap<PolicyChangeState> = {
  status: fromPolicyChangeState.reducer,
  notification: fromPolicyChangeContainer.reducer
};

export const selectPolicyChangeState = createFeatureSelector<PolicyChangeState>('policyChange');

export const selectPolicyChangeStateStatus = createSelector(
  selectPolicyChangeState,
  (state: PolicyChangeState) => state.status
);

export const selectPolicyChangePageState = createSelector(
  selectPolicyChangeState,
  (state: PolicyChangeState) => state.notification
);

export const selectPolicyChangePageError = createSelector(
  selectPolicyChangePageState,
  fromPolicyChangeContainer.getError
);

export const selectPolicyChangePageSuccess = createSelector(
  selectPolicyChangePageState,
  fromPolicyChangeContainer.getSuccess
);

export const selectPolicyChangePageLoading = createSelector(
  selectPolicyChangePageState,
  fromPolicyChangeContainer.getLoading
);

export const getSelectedPolicyChange = createSelector(
  selectPolicyChangeState,
  (state: PolicyChangeState) => state.status
);

export const getPolicyChangeCancelPath = createSelector(
  selectPolicyChangePageState,
  state => state.cancelNavigationPath
);

/** Coverage Change Selectors */

export interface CoverageChangeState {
  status: CoverageChangeRequest;
  notification: fromCoverageChangeContainer.State;
}

export interface CoverageState extends fromRoot.RootState {
  coverageChange: CoverageChangeState;
}

export const coverageChangeReducers: ActionReducerMap<CoverageChangeState> = {
  status: fromCoverageChangeState.reducer,
  notification: fromCoverageChangeContainer.reducer
};

export const selectCoverageChangeState =
  createFeatureSelector<CoverageChangeState>('coverageChange');

export const selectCoverageChangeStateStatus = createSelector(
  selectCoverageChangeState,
  (state: CoverageChangeState) => state.status
);

export const selectCoverageLimits = createSelector(
  selectCoverageChangeStateStatus,
  (state: CoverageChangeState['status']) => state.autoCoverages
);

export const getSelectedCoverageLimits = createSelector(
  selectCoverageChangeStateStatus,
  (state: CoverageChangeState['status']) => state.selectedCoverages
);

export const getQuoteDetailsForNewCoverage = createSelector(
  selectCoverageChangeStateStatus,
  (state: CoverageChangeState['status']) => state.quote
);

export const getSelectedVehicleIdentifier = createSelector(
  selectCoverageChangeStateStatus,
  (state: CoverageChangeState['status']) => {
    return state.selectedCoverages ? state.selectedCoverages.vehicleIdentifier : '';
  }
);

export const getSelectedCoverageLimitsByCode = createSelector(
  selectCoverageChangeStateStatus,
  (state: CoverageChangeState['status'], coverageCode: string) => {
    const coverage = state.selectedCoverages.riskCoverages.find(
      riskCoverage => riskCoverage.code === coverageCode
    );
    return _get(coverage, 'terms[0].selectedValue', '');
  }
);

export const getSelectedERSCoverage = createSelector(
  selectCoverageChangeStateStatus,
  (state: CoverageChangeState['status'], coverageCode: string) => {
    const coverage = state.selectedCoverages.riskCoverages.find(
      riskCoverage => riskCoverage.code === coverageCode
    );
    return _get(coverage, 'selected', '');
  }
);

export const getSelectedCollisonCoverageLimits = createSelector(
  selectCoverageChangeStateStatus,
  (state: CoverageChangeState['status']) => state.selectedCoverages
);

export const selectComprehensiveCoverageLimits = createSelector(
  selectCoverageLimits,
  (state: CoverageChangeState['status']['autoCoverages']) => {
    return state ? state['Comprehensive'].limits.split(',') : [];
  }
);

export const selectCollisionCoverageLimits = createSelector(
  selectCoverageLimits,
  (state: CoverageChangeState['status']['autoCoverages']) => {
    return state ? state['Collision'].limits.split(',') : [];
  }
);

export const selectRentalCoverageLimits = createSelector(
  selectCoverageLimits,
  (state: CoverageChangeState['status']['autoCoverages']) =>
    _get(state['Rental Reimbursement'], '', state['Rental Remimbursement']).limits.split(',')
);

export const selectCoverageChangePageState = createSelector(
  selectCoverageChangeState,
  (state: CoverageChangeState) => state.notification
);

export const selectCoverageChangePageError = createSelector(
  selectCoverageChangePageState,
  fromCoverageChangeContainer.getError
);

export const getCoverageRequestType = createSelector(
  selectCoverageChangeStateStatus,
  (state: CoverageChangeState['status']) => state.requestType
);

export const partnerModelCoverageEnabled = createSelector(
  featureFlagQuery.selectFeatureFlagEntitiesState,
  entities => !_isEmpty(entities) && entities['partner_model_coverage'].enabled
);

export const modelCoverageEnabled = createSelector(
  featureFlagQuery.selectFeatureFlagEntitiesState,
  entities => !_isEmpty(entities) && entities['model_coverage'].enabled
);
